import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Phone, ShieldCheck, Camera, UserCircle, Check, MapPin, Image, Megaphone, IdCard, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const INTERESTS = ["Coffee", "Walks", "Board Games", "Books", "Music", "Art", "Food", "Fitness", "Movies", "Startups", "Photography", "Travel"];
const TOTAL = 5;

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  // phone
  const [phone, setPhone] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [devCode, setDevCode] = useState("");
  const [code, setCode] = useState("");
  // consent
  const [consent, setConsent] = useState({ location: false, photos: false, marketing: false });
  // kyc
  const [idUrl, setIdUrl] = useState("");
  const [selfieUrl, setSelfieUrl] = useState("");
  // personality
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});   // qId -> archetype
  const [result, setResult] = useState(null);
  // profile
  const [bio, setBio] = useState("");
  const [interests, setInterests] = useState([]);
  const [isHost, setIsHost] = useState(true);
  const [city, setCity] = useState("Bengaluru");
  const CITIES = ["Bengaluru", "Mumbai", "Delhi & NCR"];

  useEffect(() => { api.get("/questionnaire").then(({ data }) => setQuestions(data.questions)); }, []);

  const sendOtp = async () => {
    if (!phone) return toast.error("Enter your phone number");
    setBusy(true);
    try { const { data } = await api.post("/auth/otp/send", { phone }); setOtpSent(true); setDevCode(data.dev_code); toast.success("OTP sent (dev mode)"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const verifyOtp = async () => {
    setBusy(true);
    try { await api.post("/auth/otp/verify", { phone, code }); toast.success("Phone verified!"); setStep(2); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const saveConsent = async () => {
    setBusy(true);
    try { await api.put("/consent", consent); setStep(3); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const submitKyc = async () => {
    if (!idUrl || !selfieUrl) return toast.error("Add both an ID document and a selfie URL");
    setBusy(true);
    try { await api.post("/kyc/submit", { id_doc_url: idUrl, selfie_url: selfieUrl }); toast.success("KYC submitted for review"); setStep(4); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const savePersonality = async () => {
    if (Object.keys(answers).length < questions.length) return toast.error("Please answer every question");
    setBusy(true);
    try { const { data } = await api.post("/personality", { answers: Object.values(answers) }); setResult(data); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const finish = async () => {
    setBusy(true);
    try {
      await api.put("/profile", { bio, interests, city, is_host: isHost, role_pref: isHost ? "both" : "guest", avatar_url: selfieUrl || undefined });
      await refresh();
      toast.success("Welcome to Saathly!");
      navigate("/swipe");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const toggle = (v) => setInterests(interests.includes(v) ? interests.filter(x => x !== v) : [...interests, v]);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-sand py-10 px-5">
      <div className="max-w-xl mx-auto">
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-charcoal/50">Step {step} of {TOTAL}</p>
          <Progress value={(step / TOTAL) * 100} className="mt-2 h-2 [&>div]:bg-terracotta" />
        </div>

        {step === 1 && (
          <Card icon={Phone} title="Verify your phone" sub="Two-step verification keeps Saathly safe.">
            {!otpSent ? (<>
              <Label className="text-charcoal font-semibold">Phone number</Label>
              <Input data-testid="otp-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98XXXXXXXX" className="mt-1.5 rounded-xl" />
              <Button data-testid="otp-send" onClick={sendOtp} disabled={busy} className="w-full mt-5 rounded-full bg-terracotta hover:bg-terracotta/90 text-white h-11">Send OTP</Button>
            </>) : (<>
              {devCode && <div className="bg-sun/20 border border-sun rounded-xl p-3 text-sm text-charcoal mb-4">Dev mode OTP: <strong data-testid="dev-otp">{devCode}</strong></div>}
              <Label className="text-charcoal font-semibold">Enter the 6-digit code</Label>
              <Input data-testid="otp-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className="mt-1.5 rounded-xl tracking-[0.3em] text-center text-lg" />
              <Button data-testid="otp-verify" onClick={verifyOtp} disabled={busy} className="w-full mt-5 rounded-full bg-terracotta hover:bg-terracotta/90 text-white h-11">Verify</Button>
            </>)}
          </Card>
        )}

        {step === 2 && (
          <Card icon={ShieldCheck} title="Your consent, your control" sub="Granular, unticked and withdrawable anytime (DPDP Act 2023).">
            {[
              { k: "location", icon: MapPin, t: "Location", d: "For distance-based matching and safety." },
              { k: "photos", icon: Image, t: "Photos", d: "Use your photos for your profile and verification." },
              { k: "marketing", icon: Megaphone, t: "Marketing", d: "Offers & tips via email/WhatsApp. Optional." },
            ].map((c) => (
              <div key={c.k} className="flex items-center justify-between bg-sand rounded-xl p-4 mb-3">
                <div className="flex items-center gap-3"><c.icon className="w-5 h-5 text-terracotta" strokeWidth={1.5} />
                  <div><p className="font-semibold text-charcoal text-sm">{c.t}</p><p className="text-xs text-charcoal/60">{c.d}</p></div></div>
                <Switch data-testid={`consent-${c.k}`} checked={consent[c.k]} onCheckedChange={(v) => setConsent({ ...consent, [c.k]: v })} className="data-[state=checked]:bg-sage" />
              </div>
            ))}
            <Button data-testid="consent-save" onClick={saveConsent} disabled={busy} className="w-full mt-3 rounded-full bg-terracotta hover:bg-terracotta/90 text-white h-11">Continue</Button>
          </Card>
        )}

        {step === 3 && (
          <Card icon={IdCard} title="Identity verification (KYC)" sub="Government ID + selfie. Reviewed by our team. (Paste image URLs for this demo.)">
            <Label className="text-charcoal font-semibold">Government ID image URL</Label>
            <Input data-testid="kyc-id" value={idUrl} onChange={(e) => setIdUrl(e.target.value)} placeholder="https://..." className="mt-1.5 rounded-xl" />
            <Label className="text-charcoal font-semibold mt-4 block">Selfie image URL (liveness)</Label>
            <Input data-testid="kyc-selfie" value={selfieUrl} onChange={(e) => setSelfieUrl(e.target.value)} placeholder="https://..." className="mt-1.5 rounded-xl" />
            <div className="flex items-center gap-2 text-xs text-charcoal/50 mt-3"><Camera className="w-4 h-4" /> Manual admin review for now.</div>
            <Button data-testid="kyc-submit" onClick={submitKyc} disabled={busy} className="w-full mt-5 rounded-full bg-terracotta hover:bg-terracotta/90 text-white h-11">Submit for review</Button>
          </Card>
        )}

        {step === 4 && (
          <Card icon={Sparkles} title="Personality check" sub="Answer 6 quick questions so we can match you with the right people.">
            {!result ? (<>
              <div className="space-y-5">
                {questions.map((q, qi) => (
                  <div key={q.id} data-testid={`pq-${q.id}`}>
                    <p className="font-semibold text-charcoal text-sm mb-2">{qi + 1}. {q.q}</p>
                    <div className="grid grid-cols-1 gap-2">
                      {q.options.map((o) => (
                        <button key={o.id} data-testid={`pq-${q.id}-${o.id}`} type="button"
                          onClick={() => setAnswers({ ...answers, [q.id]: o.archetype })}
                          className={`text-left text-sm px-4 py-2.5 rounded-xl border transition-colors duration-200 ${answers[q.id] === o.archetype ? "bg-terracotta text-white border-terracotta" : "bg-white text-charcoal/80 border-border hover:border-terracotta/40"}`}>
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <Button data-testid="personality-submit" onClick={savePersonality} disabled={busy} className="w-full mt-6 rounded-full bg-terracotta hover:bg-terracotta/90 text-white h-11">See my result</Button>
            </>) : (
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full bg-sun/30 flex items-center justify-center mx-auto mb-3"><Sparkles className="w-8 h-8 text-charcoal" /></div>
                <p className="text-xs font-bold uppercase tracking-wider text-charcoal/50">You are</p>
                <p className="font-display text-3xl font-black text-terracotta mt-1" data-testid="personality-result">{result.personality_type}</p>
                <p className="text-charcoal/70 mt-2 max-w-sm mx-auto">{result.personality_blurb}</p>
                <Button data-testid="personality-next" onClick={() => setStep(5)} className="w-full mt-6 rounded-full bg-terracotta hover:bg-terracotta/90 text-white h-11">Continue</Button>
              </div>
            )}
          </Card>
        )}

        {step === 5 && (
          <Card icon={UserCircle} title="Your profile" sub="Keep it simple — your name & age are set. Add a short bio.">
            <div className="bg-sand rounded-xl p-4 mb-4">
              <p className="font-display text-lg font-bold text-charcoal">{user?.name}{user?.dob ? `, ${new Date().getFullYear() - new Date(user.dob).getFullYear()}` : ""}</p>
              {result && <p className="text-sm text-terracotta font-semibold">{result.personality_type}</p>}
            </div>
            <Label className="text-charcoal font-semibold">Short bio</Label>
            <Textarea data-testid="prof-bio" value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Coffee lover & weekend trekker. Here for good conversation." className="mt-1.5 rounded-xl" />
            <Label className="text-charcoal font-semibold mt-4 block">Your city</Label>
            <select data-testid="prof-city" value={city} onChange={(e) => setCity(e.target.value)} className="mt-1.5 w-full rounded-xl border border-border p-2.5 text-sm bg-white">
              {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <p className="text-xs text-charcoal/50 mt-1.5">Saathly is currently live in Bengaluru, Mumbai and Delhi & NCR.</p>
            <Label className="text-charcoal font-semibold mt-4 block">A few interests (optional)</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {INTERESTS.map((it) => (
                <button key={it} data-testid={`interest-${it}`} type="button" onClick={() => toggle(it)}
                  className={`text-sm font-semibold px-3 py-1.5 rounded-full border transition-colors duration-200 ${interests.includes(it) ? "bg-terracotta text-white border-terracotta" : "bg-white text-charcoal/70 border-border"}`}>{it}</button>
              ))}
            </div>
            <label className="flex items-center justify-between bg-sand rounded-xl p-4 mt-4">
              <div><p className="font-semibold text-charcoal text-sm">Open to being booked as a host</p><p className="text-xs text-charcoal/60">Others can book public hangouts with you.</p></div>
              <Switch data-testid="prof-host" checked={isHost} onCheckedChange={setIsHost} className="data-[state=checked]:bg-sage" />
            </label>
            <Button data-testid="prof-finish" onClick={finish} disabled={busy} className="w-full mt-6 rounded-full bg-terracotta hover:bg-terracotta/90 text-white h-11"><Check className="w-4 h-4 mr-1" /> Start swiping</Button>
          </Card>
        )}
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, sub, children }) {
  return (
    <div className="bg-white rounded-2xl border border-border p-8 shadow-sm">
      <div className="w-12 h-12 rounded-full bg-terracotta/10 flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-terracotta" strokeWidth={1.5} />
      </div>
      <h2 className="font-display text-2xl font-bold text-charcoal">{title}</h2>
      <p className="text-sm text-charcoal/60 mt-1 mb-6">{sub}</p>
      {children}
    </div>
  );
}
